import { translateError } from "@/lib/errors";
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Upload, Library, Music2 } from "lucide-react";
import { Layout } from "@/components/hud/Layout";
import { Button } from "@/components/primitives/Button";
import { MontageItemRow } from "@/components/montage/MontageItemRow";
import { ClipPicker } from "@/components/montage/ClipPicker";
import { RenderPanel } from "@/components/montage/RenderPanel";
import { WatermarkPanel, MusicPanel } from "@/components/montage/MontageExtras";
import { TextPanel } from "@/components/montage/TextPanel";
import {
  getMontage, getRender, addFromClip, removeItem, reorderItems, trimItem, startRender, updateMontage,
  uploadMontageVideo, getUploadLimits, UploadError, UploadStillProcessing, fmtDuration,
  type Montage, type MontageAspectRatio, type UploadProgress,
} from "@/lib/montageApi";

const errMsg = (err: any, fallback: string) => translateError(err?.response?.data?.detail) || fallback;

export default function MontageEditor() {
  const { t } = useTranslation();
  const { id = "" } = useParams();
  const [m, setM] = useState<Montage | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [picker, setPicker] = useState(false);
  const [upload, setUpload] = useState<UploadProgress | null>(null);
  const [maxBytes, setMaxBytes] = useState<number | null>(null);
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    getMontage(id).then(setM).catch(() => setNotFound(true));
    getUploadLimits().then((l) => setMaxBytes(l.max_upload_bytes)).catch(() => {});
  }, [id]);
  const tooLargeMsg = (limit: number) => t("montageEditor.fileTooLarge", { gb: (limit / 1024 ** 3).toLocaleString(undefined, { maximumFractionDigits: 1 }) });

  const active = m?.render?.status === "IN_ATTESA" || m?.render?.status === "RENDERING";

  useEffect(() => {
    if (!active) return;
    const t = setInterval(() => {
      getRender(id).then((r) => setM((prev) => (prev ? { ...prev, render: r } : prev))).catch(() => {});
    }, 3000);
    return () => clearInterval(t);
  }, [active, id]);

  const run = useCallback(async (p: Promise<Montage>, ok?: string, fallback = t("errors.generic")) => {
    try {
      setM(await p);
      if (ok) toast.success(ok);
    } catch (err) {
      toast.error(errMsg(err, fallback));
      throw err;
    }
  }, [t]);

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!/\.(mp4|mov|mkv)$/i.test(f.name)) {
      toast.error(t("montageEditor.badFormat"));
      return;
    }
    if (maxBytes !== null && f.size > maxBytes) {  // same limit as the backend: no init, no chunks
      toast.error(tooLargeMsg(maxBytes));
      return;
    }
    setUpload({ pct: 0 });
    try {
      setM(await uploadMontageVideo(id, f, setUpload));
      toast.success(t("montageEditor.videoUploaded"));
    } catch (err) {
      if (err instanceof UploadStillProcessing) toast.info(t("montageEditor.uploadTakingLonger"), { duration: 10000 });
      else if (err instanceof UploadError) toast.error(translateError(err.detail) || t("errors.uploadFailed"));
      else if ((err as any)?.response?.status === 413 && maxBytes) toast.error(tooLargeMsg(maxBytes));
      else toast.error(errMsg(err, t("errors.uploadFailed")));
    } finally {
      setUpload(null);
    }
  };

  const move = (from: number, to: number) => {
    if (!m || to < 0 || to >= m.items.length || from === to) return;
    const ids = m.items.map((i) => i.id);
    const [moved] = ids.splice(from, 1);
    ids.splice(to, 0, moved);
    run(reorderItems(id, ids), undefined, t("scenes.reorderFailed")).catch(() => {});
  };

  const onStartRender = async () => {
    try {
      const r = await startRender(id);
      setM((prev) => (prev ? { ...prev, render: r } : prev));
      toast.success(t("montageEditor.renderStarted"));
    } catch (err) {
      toast.error(errMsg(err, t("montageEditor.renderStartFailed")));
    }
  };

  const setAspect = async (ratio: MontageAspectRatio) => {
    if (!m || m.aspect_ratio === ratio) return;
    const previous = m.aspect_ratio;
    setM({ ...m, aspect_ratio: ratio });
    try {
      const updated = await updateMontage(id, { aspect_ratio: ratio });
      setM(updated);
    } catch (err) {
      setM((prev) => (prev ? { ...prev, aspect_ratio: previous } : prev));
      toast.error(errMsg(err, t("scenes.formatNotSaved")));
    }
  };

  if (notFound) {
    return (
      <Layout>
        <main className="mx-auto max-w-3xl px-6 py-24 text-center text-muted-foreground" data-testid="montage-not-found">
          {t("errors.montageNotFound")} <Link to="/montage" className="text-[#2E7BFF]">{t("montageEditor.backToMontage")}</Link>
        </main>
      </Layout>
    );
  }
  if (!m) {
    return (
      <Layout>
        <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      </Layout>
    );
  }

  const busy = upload !== null;
  const uploadLabel = !upload ? t("montageEditor.uploadVideo")
    : upload.phase ? t("montageEditor.processingVideo") : t("montageEditor.uploadingPct", { pct: upload.pct });

  return (
    <Layout>
      <main data-testid="montage-editor" className="relative mx-auto max-w-6xl px-6 pb-32 pt-12 sm:px-10">
        <Link to="/montage" data-testid="montage-back" className="mb-8 inline-flex items-center gap-2 font-display text-[11px] font-semibold uppercase tracking-[0.26em] text-muted-foreground transition-colors hover:text-[#7FD4FF]">
          <ArrowLeft className="h-3.5 w-3.5" /> Tutti i Montage
        </Link>
        <div className="mb-10 flex flex-wrap items-end justify-between gap-6">
          <div className="min-w-0">
            <span className="eyebrow">{t("montageEditor.eyebrow")}</span>
            <h1 data-testid="montage-editor-title" className="headline-shadow mt-3 truncate font-display text-4xl font-bold uppercase tracking-wide text-foreground sm:text-5xl">{m.title}</h1>
            <p className="mt-3 flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
              <span className="glass inline-flex items-center gap-2 rounded-full px-3 py-1 !shadow-none"><span className="font-display font-bold text-foreground">{m.items.length}</span> video</span>
              <span className="glass inline-flex items-center gap-2 rounded-full px-3 py-1 !shadow-none">{t("montageEditor.duration")} <span data-testid="montage-editor-total" className="font-display font-bold text-foreground">{fmtDuration(m.total_duration)}</span></span>
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button data-testid="montage-add-clip-btn" variant="secondary" onClick={() => setPicker(true)} disabled={busy || active}>
              <Library className="h-4 w-4" /> Dai miei clip
            </Button>
            <Button data-testid="montage-upload-btn" onClick={() => fileRef.current?.click()} disabled={busy || active}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              {uploadLabel}
            </Button>
            <input ref={fileRef} data-testid="montage-file-input" type="file" accept=".mp4,.mov,.mkv,video/mp4,video/quicktime,video/x-matroska" className="hidden" onChange={onFile} />
            <Link to="/music" data-testid="montage-go-to-music" className="inline-flex w-full items-center justify-end gap-1.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground transition-colors hover:text-[#FF7AD5]">
              <Music2 className="h-3.5 w-3.5" /> {t("montageEditor.goToMusic")}
            </Link>
          </div>
        </div>

        <div data-testid="montage-aspect-panel" className="glass mb-8 flex flex-wrap items-center gap-4 rounded-2xl px-5 py-4">
          <span className="text-[11px] font-semibold uppercase tracking-[0.26em] text-[#7FD4FF]">{t("montageEditor.finalFormat")}</span>
          <div role="radiogroup" aria-label="Formato video finale" className="seg">
            {[
              { v: "9:16" as const, label: `${t("format.vertical")} 9:16`, size: "720×1280", testid: "montage-aspect-9-16" },
              { v: "16:9" as const, label: `${t("format.horizontal")} 16:9`, size: "1280×720", testid: "montage-aspect-16-9" },
            ].map((opt) => {
              const selected = m.aspect_ratio === opt.v;
              return (
                <button
                  key={opt.v}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  data-testid={opt.testid}
                  onClick={() => setAspect(opt.v)}
                  disabled={busy || !!active}
                  className={"seg-btn " + (selected ? "seg-btn-active" : "disabled:cursor-not-allowed disabled:opacity-50")}
                >
                  <span>{opt.label}</span>
                  <span className="ml-2 hidden text-[10px] uppercase tracking-widest opacity-75 sm:inline">{opt.size}</span>
                </button>
              );
            })}
          </div>
          {active && <span className="text-xs text-muted-foreground">{t("montageEditor.editableAfterRender")}</span>}
        </div>

        {m.items.length === 0 ? (
          <div data-testid="montage-timeline-empty" className="glass mb-8 rounded-2xl border-dashed !border-[#2E7BFF]/30 py-16 text-center text-sm text-muted-foreground">
            {t("montageEditor.empty")}
          </div>
        ) : (
          <ul data-testid="montage-timeline" className="mb-8 space-y-3">
            {m.items.map((it, idx) => (
              <MontageItemRow
                key={it.id}
                montageId={id}
                item={it}
                index={idx}
                count={m.items.length}
                disabled={busy || !!active}
                dragging={dragIdx === idx}
                onDragStart={() => setDragIdx(idx)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => { if (dragIdx !== null) move(dragIdx, idx); setDragIdx(null); }}
                onMove={(dir) => move(idx, idx + dir)}
                onRemove={() => run(removeItem(id, it.id), t("montageEditor.itemRemoved")).catch(() => {})}
                onTrim={(i, o) => run(trimItem(id, it.id, i, o), t("montageEditor.trimSaved"), t("montageEditor.trimInvalid")).catch(() => {})}
              />
            ))}
          </ul>
        )}

        <div className="mb-8 grid gap-5 lg:grid-cols-2">
          <WatermarkPanel m={m} onUpdate={setM} disabled={busy || !!active} />
          <MusicPanel m={m} onUpdate={setM} disabled={busy || !!active} />
          <div className="min-w-0 lg:col-span-2">
            <TextPanel m={m} onUpdate={setM} disabled={busy || !!active} />
          </div>
        </div>

        <RenderPanel
          render={m.render}
          title={m.title}
          canRender={m.items.length > 0 && !busy}
          totalDuration={m.total_duration}
          onStart={onStartRender}
        />

        {picker && (
          <ClipPicker
            onClose={() => setPicker(false)}
            onPick={async (clipId) => {
              await run(addFromClip(id, clipId), t("montageEditor.clipAdded"), t("montageEditor.importFailed")).catch(() => {});
            }}
          />
        )}
      </main>
    </Layout>
  );
}
